import React, { useState, forwardRef, useRef, useEffect } from "react";
import clsx from "clsx";
import { useStore } from "react-redux";

import { makeStyles, alpha } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import Fade from "@material-ui/core/Fade";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import { colors } from "src/theme";
import { formatDollars } from "src/utils";
import InfoTooltip from "src/components/InfoTooltip";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import StatementDrill, { StatementDrillType } from "./StatementDrill";
import ReportExplanationText from "./ReportExplanationText";
import type { ProfitLossComparisonReport as ProfitLossComparisonReportType } from "./report";

import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { printReport } from "./pdf";

const StatementDrillDownCompareTooltipText = "Click in number to view details";

type StatementDrillTypeWithReport =
  | (StatementDrillType & { report?: number })
  | null;

function getSortedKeys(objs: Record<string, any>[]): string[] {
  const keys = [
    ...new Set(
      objs.reduce<string[]>(
        (keysAccumulator, object) => [
          ...keysAccumulator,
          ...Object.keys(object),
        ],
        []
      )
    ),
  ];
  keys.sort((a, b) => a.localeCompare(b));

  return keys;
}

function sumRecordValuesAcrossPeriods(
  data: ProfitLossComparisonReportType["data"],
  getRecord: (
    period: ProfitLossComparisonReportType["data"][number]
  ) => Record<string, number | undefined>,
  key: string
): number {
  return data.reduce((sum, period) => sum + (getRecord(period)[key] || 0), 0);
}

function sumFieldAcrossPeriods(
  data: ProfitLossComparisonReportType["data"],
  field:
    | "totalSales"
    | "totalCOGS"
    | "totalExpenses"
    | "grossProfit"
    | "netProfitNoIncomeTax"
): number {
  return data.reduce((sum, period) => sum + period[field], 0);
}

const useHeaderStyles = makeStyles((theme) => ({
  reportType: {
    marginRight: theme.spacing(2),
    "& button": {
      lineHeight: 1,
    },
  },
}));

type GroupByType = "month" | "quarter";

interface ProfitLossComparisonReportHeaderProps {
  groupBy: GroupByType;
  onGroupByChange: (g: GroupByType) => void;
  onDownloadClick?: () => void;
}

function ProfitLossComparisonReportHeader({
  groupBy,
  onGroupByChange,
  onDownloadClick,
}: ProfitLossComparisonReportHeaderProps) {
  const recordEvent = useRecordEvent();

  const classes = useHeaderStyles();

  return (
    <Title
      actions={
        <>
          <Fade in={true} timeout={600} style={{ transitionDelay: "800ms" }}>
            <ToggleButtonGroup
              className={classes.reportType}
              value={groupBy}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  recordEvent({
                    type: "feature-adoption",
                    feature: "pl-comparison-feature",
                    element: "toggle-group-by",
                    value,
                  });
                  onGroupByChange(value);
                }
              }}
            >
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="quarter">Quarter</ToggleButton>
            </ToggleButtonGroup>
          </Fade>
          {onDownloadClick && (
            <DownloadButton onClick={onDownloadClick as any} />
          )}
        </>
      }
    >
      Profit/Loss Comparison
      <InfoTooltip text={<ReportExplanationText />} />
    </Title>
  );
}

const useBodyStyles = makeStyles((theme) => ({
  table: {
    margin: theme.spacing(3, "auto", 4),
    width: "100%",
    borderSpacing: 0,
    borderCollapse: "collapse",
    overflowX: "auto",
    display: "block",
    "& td:first-child, & th:first-child": {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
      paddingRight: theme.spacing(2),
      position: "sticky",
      left: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: 1,
    },
    "& td:last-child, & th:last-child": {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
    "& td, & th": {
      borderRadius: theme.shape.borderRadius,
      minWidth: 80,
    },
  },
  left: {
    textAlign: "left",
  },
  right: {
    textAlign: "right",
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  detail: {
    paddingLeft: theme.spacing(2),
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  blue: {
    color: colors.blueColor,
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  space: {
    padding: theme.spacing(0, 2),
  },
  pointer: {
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  highlightedRow: {
    "& td": {
      background: alpha(theme.palette.primary.main, 0.15),
    },
    "&:hover td": {
      background: alpha(theme.palette.primary.main, 0.25),
    },
  },
  highlightedCell: {
    background: alpha(theme.palette.primary.main, 0.15),
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.25),
    },
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
}));

interface ProfitLossComparisonReportBodyProps {
  report: ProfitLossComparisonReportType;
  onStatementDrillChange: (st: StatementDrillTypeWithReport) => void;
  statementDrill: StatementDrillTypeWithReport;
}

const ProfitLossComparisonReportBody = forwardRef(
  (
    {
      report,
      onStatementDrillChange,
      statementDrill,
    }: ProfitLossComparisonReportBodyProps,
    ref
  ) => {
    const recordEvent = useRecordEvent();
    const classes = useBodyStyles();
    const columnCount = report.data.length + 2;

    return (
      <Box className={classes.table} {...{ ref }} component="table">
        <thead>
          <tr>
            <th />
            {report.data.map((data, i) => (
              <th
                className={clsx(classes.nowrap, classes.space, classes.title)}
                key={i}
              >
                {data.reportDetails.dateRange}
              </th>
            ))}
            <th
              className={clsx(
                classes.nowrap,
                classes.space,
                classes.title,
                classes.bold
              )}
            >
              Grand Total
            </th>
          </tr>
          <tr>
            <th className={clsx(classes.bold, classes.left)}>Income</th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalSales)}
              </th>
            ))}
            <th className={clsx(classes.bold, classes.right)}>
              {formatDollars(sumFieldAcrossPeriods(report.data, "totalSales"))}
            </th>
          </tr>
        </thead>
        <tbody>
          {getSortedKeys(report.data.map((d) => d.salesByDepartment)).map(
            (department) => {
              const selectedRow =
                statementDrill?.type === "sales" &&
                statementDrill.platform === department;
              return (
                <Tooltip
                  key={department}
                  title={StatementDrillDownCompareTooltipText}
                  placement="top"
                >
                  <tr
                    className={clsx(classes.pointer)}
                    onClick={(e) => {
                      recordEvent({
                        type: "feature-adoption",
                        feature: "pl-comparison-feature",
                        element: "click-to-view-details",
                      });
                      const reportIndex = parseInt(
                        (e.target as HTMLDivElement).getAttribute(
                          "data-index"
                        ) || "",
                        10
                      );

                      onStatementDrillChange(
                        selectedRow && reportIndex === statementDrill?.report
                          ? null
                          : {
                              type: "sales",
                              platform: department,
                              ...(isNaN(reportIndex)
                                ? {}
                                : { report: reportIndex }),
                            }
                      );
                    }}
                  >
                    <td className={classes.detail}>{department}</td>
                    {report.data.map((data, i) => {
                      const value = data.salesByDepartment[department] || 0;
                      const selected =
                        selectedRow && statementDrill?.report === i;
                      return (
                        <td
                          key={`${i}-${value}`}
                          className={clsx(
                            classes.right,
                            selected && classes.highlightedCell,
                            classes.pointer
                          )}
                          data-index={i}
                        >
                          {formatDollars(value)}
                        </td>
                      );
                    })}
                    <td className={classes.right}>
                      {formatDollars(
                        sumRecordValuesAcrossPeriods(
                          report.data,
                          (period) => period.salesByDepartment,
                          department
                        )
                      )}
                    </td>
                  </tr>
                </Tooltip>
              );
            }
          )}
        </tbody>
        <thead>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Cost of Sales
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalCOGS)}
              </th>
            ))}
            <th className={clsx(classes.bold, classes.right)}>
              {formatDollars(sumFieldAcrossPeriods(report.data, "totalCOGS"))}
            </th>
          </tr>
        </thead>
        <tbody>
          {getSortedKeys(report.data.map((d) => d.COGSDetail)).map(
            (costType) => {
              const selectedRow =
                statementDrill?.type === "expense" &&
                statementDrill.name === costType;

              return (
                <Tooltip
                  key={costType}
                  title={StatementDrillDownCompareTooltipText}
                  placement="top"
                >
                  <tr
                    className={clsx(classes.pointer)}
                    onClick={(e) => {
                      recordEvent({
                        type: "feature-adoption",
                        feature: "pl-comparison-feature",
                        element: "click-to-view-details",
                      });
                      const reportIndex = parseInt(
                        (e.target as HTMLDivElement).getAttribute(
                          "data-index"
                        ) || "",
                        10
                      );

                      onStatementDrillChange(
                        selectedRow && reportIndex === statementDrill?.report
                          ? null
                          : {
                              type: "expense",
                              name: costType,
                              ...(isNaN(reportIndex)
                                ? {}
                                : { report: reportIndex }),
                            }
                      );
                    }}
                  >
                    <td className={classes.detail}>{costType}</td>
                    {report.data.map((data, i) => {
                      const value = data.COGSDetail[costType] || 0;
                      const selected =
                        selectedRow && statementDrill?.report === i;

                      return (
                        <td
                          key={`${i}-${value}`}
                          className={clsx(
                            classes.right,
                            selected && classes.highlightedCell,
                            classes.pointer
                          )}
                          data-index={i}
                        >
                          {formatDollars(value)}
                        </td>
                      );
                    })}
                    <td className={classes.right}>
                      {formatDollars(
                        sumRecordValuesAcrossPeriods(
                          report.data,
                          (period) => period.COGSDetail,
                          costType
                        )
                      )}
                    </td>
                  </tr>
                </Tooltip>
              );
            }
          )}
          <tr>
            <td className={classes.divider} colSpan={columnCount} />
          </tr>
        </tbody>
        <thead>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Gross Profit
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.grossProfit)}
              </th>
            ))}
            <th className={clsx(classes.bold, classes.right)}>
              {formatDollars(sumFieldAcrossPeriods(report.data, "grossProfit"))}
            </th>
          </tr>
          <tr>
            <th className={clsx(classes.bold, classes.left, classes.nowrap)}>
              Other Business Expenses
            </th>
            {report.data.map((data, i) => (
              <th className={clsx(classes.bold, classes.right)} key={i}>
                {formatDollars(data.totalExpenses)}
              </th>
            ))}
            <th className={clsx(classes.bold, classes.right)}>
              {formatDollars(
                sumFieldAcrossPeriods(report.data, "totalExpenses")
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {getSortedKeys(report.data.map((d) => d.expenseDetail)).map(
            (expenseType) => {
              const selectedRow =
                statementDrill?.type === "transaction" &&
                statementDrill.name === expenseType;
              return (
                <Tooltip
                  key={expenseType}
                  title={StatementDrillDownCompareTooltipText}
                  placement="top"
                >
                  <tr
                    className={clsx(classes.pointer)}
                    onClick={(e) => {
                      recordEvent({
                        type: "feature-adoption",
                        feature: "pl-comparison-feature",
                        element: "click-to-view-details",
                      });
                      const reportIndex = parseInt(
                        (e.target as HTMLDivElement).getAttribute(
                          "data-index"
                        ) || "",
                        10
                      );

                      onStatementDrillChange(
                        selectedRow && reportIndex === statementDrill?.report
                          ? null
                          : {
                              type: "transaction",
                              name: expenseType,
                              ...(isNaN(reportIndex)
                                ? {}
                                : { report: reportIndex }),
                            }
                      );
                    }}
                  >
                    <td className={classes.detail}>{expenseType}</td>
                    {report.data.map((data, i) => {
                      const value = data.expenseDetail[expenseType] || 0;
                      const selected =
                        selectedRow && statementDrill?.report === i;
                      return (
                        <td
                          key={`${i}-${value}`}
                          className={clsx(
                            classes.right,
                            selected && classes.highlightedCell,
                            classes.pointer
                          )}
                          data-index={i}
                        >
                          {formatDollars(value)}
                        </td>
                      );
                    })}
                    <td className={classes.right}>
                      {formatDollars(
                        sumRecordValuesAcrossPeriods(
                          report.data,
                          (period) => period.expenseDetail,
                          expenseType
                        )
                      )}
                    </td>
                  </tr>
                </Tooltip>
              );
            }
          )}
        </tbody>
        <tbody>
          <tr>
            <td className={classes.divider} colSpan={columnCount} />
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th className={clsx(classes.left, classes.bold)}>
              Operating Profit
            </th>
            {report.data.map((data, i) => (
              <th key={i} className={clsx(classes.bold, classes.right)}>
                {formatDollars(data.netProfitNoIncomeTax)}
              </th>
            ))}
            <th className={clsx(classes.bold, classes.right)}>
              {formatDollars(
                sumFieldAcrossPeriods(report.data, "netProfitNoIncomeTax")
              )}
            </th>
          </tr>
        </tfoot>
      </Box>
    );
  }
);

interface ProfitLossComparisonReportContentProps {
  report: ProfitLossComparisonReportType;
  printable?: boolean;
  groupBy: GroupByType;
  statementDrill?: StatementDrillTypeWithReport;
  onStatementDrillChange?: (st: StatementDrillTypeWithReport) => void;
  onGroupByChange?: (g: GroupByType) => void;
  onDownloadClick?: () => void;
  statementDrillRef?: React.Ref<HTMLDivElement>;
}

function ProfitLossComparisonReportContent({
  report,
  printable = false,
  groupBy,
  statementDrill = null,
  onStatementDrillChange = () => {},
  onGroupByChange,
  onDownloadClick,
  statementDrillRef,
}: ProfitLossComparisonReportContentProps) {
  return (
    <>
      <CompanyInfo forPrint={printable} />
      {printable ? (
        <Title>Profit/Loss Comparison</Title>
      ) : (
        <ProfitLossComparisonReportHeader
          groupBy={groupBy}
          onGroupByChange={onGroupByChange!}
          onDownloadClick={onDownloadClick}
        />
      )}
      <ProfitLossComparisonReportBody
        report={report}
        statementDrill={statementDrill}
        onStatementDrillChange={onStatementDrillChange}
      />
      {!printable && (
        <div ref={statementDrillRef}>
          <StatementDrill
            data={report.data[statementDrill?.report ?? 0]}
            type={statementDrill}
            onClose={() => onStatementDrillChange(null)}
          />
        </div>
      )}
    </>
  );
}

export interface ProfitLossComparisonReportProps {
  report: ProfitLossComparisonReportType;
  onGroupByChange: (groupBy: GroupByType) => void;
}

function ProfitLossComparisonReport({
  report,
  onGroupByChange,
}: ProfitLossComparisonReportProps) {
  const store = useStore();
  const [groupBy, setGroupBy] = useState<GroupByType>(report.groupBy);
  const [statementDrill, setStatementDrill] =
    useState<StatementDrillTypeWithReport>(null);
  const statementDrillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setGroupBy(report.groupBy);
  }, [report.groupBy]);

  const key = report.data.map((d) => d.reportDetails.titleFull).join("-");
  useEffect(() => {
    setStatementDrill(null);
  }, [key]);

  // Scroll to statement drill when it opens
  useEffect(() => {
    if (statementDrill && statementDrillRef.current) {
      const timeout = setTimeout(() => {
        statementDrillRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [statementDrill]);

  const handleDownloadReport = async () => {
    await printReport(
      <Box mx={1}>
        <ProfitLossComparisonReportContent
          report={report}
          printable
          groupBy={groupBy}
        />
      </Box>,
      {
        store,
        title: `profit-loss-comparison-${report.groupBy}.pdf`,
        orientation:
          4 < report.data.length && report.data.length <= 6
            ? "landscape"
            : "portrait",
        rotate: 6 < report.data.length,
      }
    );
  };

  const handleGroupByChange = (newGroupBy: GroupByType) => {
    setGroupBy(newGroupBy);
    setStatementDrill(null);
    onGroupByChange(newGroupBy);
  };

  return (
    <Box component={Paper} p={2} mt={2} width="100%">
      <ProfitLossComparisonReportContent
        report={report}
        groupBy={groupBy}
        statementDrill={statementDrill}
        onStatementDrillChange={(st) => setStatementDrill(st)}
        onGroupByChange={handleGroupByChange}
        onDownloadClick={() => handleDownloadReport()}
        statementDrillRef={statementDrillRef}
      />
    </Box>
  );
}

export default ProfitLossComparisonReport;
